import { envConfig } from '../config/env';
import { logApiCall, logError } from './logger';

export interface RedirectConfig {
  url: string;
  params: Record<string, string>;
  auth: 'Y' | 'N';
  alpid: number;
}

/**
 * Fetches redirect config for public links (no authentication required)
 * @param keyword - The link keyword
 * @param clubCode - The club code (required)
 */
export const fetchPublicConfig = async (
  keyword: string,
  clubCode: string
): Promise<RedirectConfig> => {
  const url = `${envConfig.apiBaseUrl}/links/config/public/${encodeURIComponent(keyword)}?clubCode=${encodeURIComponent(clubCode)}`;
  const startTime = Date.now();

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // Include cookies for clubCode fallback
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const durationMs = Date.now() - startTime;

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: 'Request failed' }));
      logApiCall(url, 'GET', response.status, durationMs, false);
      throw new ApiError(response.status, errorBody.error || `HTTP ${response.status}`);
    }

    logApiCall(url, 'GET', response.status, durationMs, true);
    return response.json();
  } catch (err) {
    if (!(err instanceof ApiError)) {
      const durationMs = Date.now() - startTime;
      logError('NetworkError', err instanceof Error ? err.message : 'Network request failed');
      logApiCall(url, 'GET', 0, durationMs, false);
    }
    throw err;
  }
};

/**
 * Fetches redirect config for authenticated links
 * @param keyword - The link keyword
 * @param accessToken - The OAuth access token
 */
export const fetchAuthConfig = async (
  keyword: string,
  accessToken: string
): Promise<RedirectConfig> => {
  const url = `${envConfig.apiBaseUrl}/links/config/auth/${encodeURIComponent(keyword)}`;
  const startTime = Date.now();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const durationMs = Date.now() - startTime;

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: 'Request failed' }));
      logApiCall(url, 'GET', response.status, durationMs, false);
      throw new ApiError(response.status, errorBody.error || `HTTP ${response.status}`);
    }

    logApiCall(url, 'GET', response.status, durationMs, true);
    return response.json();
  } catch (err) {
    if (!(err instanceof ApiError)) {
      const durationMs = Date.now() - startTime;
      logError('NetworkError', err instanceof Error ? err.message : 'Network request failed');
      logApiCall(url, 'GET', 0, durationMs, false);
    }
    throw err;
  }
};

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  get isBadRequest(): boolean {
    return this.statusCode === 400;
  }
}
